import { observer } from 'mobx-react-lite';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list/lib/flights';
import loc, { format } from 'owa-localize';
import {
    favoritesHeaderText,
    favoritesWithAccountNameHeaderText,
} from 'owa-locstrings/lib/strings/favoritesheadertext.locstring.json';
import getAccountSourceListStore from 'owa-account-source-list-store/lib/store/accountSourceListStore';
import { lazyToggleFavoritesTreeExpansion } from 'owa-mail-favorites-store';
import type { FolderTextFieldViewState } from 'owa-mail-folder-store/lib/store/schema/FolderTextFieldViewState';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { trace } from 'owa-trace';
import TreeNode from 'owa-tree-node/lib/components/TreeNode';
import * as React from 'react';
import FavoritesList from './FavoritesList';

export interface FavoritesProps extends React.HTMLProps<HTMLDivElement> {
    withContextMenuFolderId?: string;
    folderTextFieldViewState?: FolderTextFieldViewState;
    setSize: number;
    positionInSet: number;
}

export default observer(function Favorites(props: FavoritesProps) {
    const ariaProps: AriaProperties = {
        role: AriaRoles.tree,
    };
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2532 (32,38): Object is possibly 'undefined'.
    // @ts-expect-error
    const isFavoritesTreeExpanded = !getUserConfiguration().UserOptions
        .IsFavoritesFolderTreeCollapsed;
    const selectedAccountSourceId = getAccountSourceListStore().selectedSourceId;
    return (
        <div
            style={props.style}
            className={props.className}
            {...generateDomPropertiesForAria(ariaProps)}>
            {renderFavoriteRoot(
                isFavoritesTreeExpanded,
                selectedAccountSourceId,
                props.setSize,
                props.positionInSet
            )}
            {isFavoritesTreeExpanded && renderFavoritesList()}
        </div>
    );
});

function onClick(evt: React.MouseEvent<unknown>) {
    evt.stopPropagation();
    lazyToggleFavoritesTreeExpansion.importAndExecute();
}

// We disable the context menu on the root.
function onRootNodeContextMenu(evt: React.MouseEvent<unknown>) {
    evt.preventDefault();
}

function renderFavoritesList(): JSX.Element {
    return <FavoritesList key="favoriteFolderList" />;
}

function getSelectedEmailAddress(selectedAccountSourceId: string) {
    let account = undefined;
    const accountSources = getAccountSourceListStore().sources;
    if (accountSources.length > 1 && isMonarchMultipleAccountsEnabled()) {
        if (selectedAccountSourceId) {
            const selectedAccounts = accountSources.filter(
                account => account.sourceId === selectedAccountSourceId
            );

            if (selectedAccounts.length > 1) {
                trace.warn(
                    `getSelectedEmailAddress: Found ${selectedAccounts.length} duplicated accounts with same Id: ${selectedAccountSourceId}`
                );
            } else if (selectedAccounts.length === 0) {
                trace.warn(
                    `getSelectedEmailAddress: Account not found for sourceId: ${selectedAccountSourceId}`
                );
            } else {
                account = selectedAccounts[0];
            }
        }
    }

    return account?.m365mailbox?.emailAddress ?? account?.displayName;
}

function renderFavoriteRoot(
    isFavoritesTreeExpanded: boolean,
    selectedAccountSourceId: string,
    setSize?: number,
    positionInSet?: number
): JSX.Element {
    const selectedEmailAddress = getSelectedEmailAddress(selectedAccountSourceId);
    const displayName = selectedEmailAddress
        ? format(loc(favoritesWithAccountNameHeaderText), selectedEmailAddress)
        : loc(favoritesHeaderText);

    // We cannot use orderedFavoritesNodeIds here, because user might delete favorites on other client and the list hasn't been updated.
    // favoritesFolderNodes and favoritesPersonaNodes are the filtered nodes which represent the nodes that we could show.
    return (
        <TreeNode
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2322 (109,66): Type '(evt: MouseEvent<unknown, MouseEvent>) => void' is not assignable to type '(evt: MouseEvent<unknown, MouseEvent> | KeyboardEvent) => void'.
            // @ts-expect-error
            chevronProps={{ isExpanded: isFavoritesTreeExpanded, onClick: onClick }}
            displayName={displayName}
            depth={0}
            isRootNode={true}
            isSelected={false}
            key="favoritesRoot"
            onClick={onClick}
            onContextMenu={onRootNodeContextMenu}
            setSize={setSize}
            positionInSet={positionInSet}
        />
    );
}
